import React, { useCallback, useEffect, useState } from "react";
import api, { formatEUR, formatApiErrorDetail } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function CarTypes() {
  const { user } = useAuth();
  const canManage =
    user?.role === "admin" || user?.permissions?.can_manage_car_types;
  const [items, setItems] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/car-types");
    setItems(data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setName("");
    setPrice("");
    setDescription("");
    setOpenForm(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setName(it.name);
    setPrice(String(it.price));
    setDescription(it.description || "");
    setOpenForm(true);
  };

  const onSave = async () => {
    const n = parseFloat(price);
    if (!name.trim() || !(n >= 0)) {
      toast.error("Indica nombre y precio válido");
      return;
    }
    try {
      const payload = { name: name.trim(), price: n, description };
      if (editing) await api.put(`/car-types/${editing.id}`, payload);
      else await api.post("/car-types", payload);
      toast.success(editing ? "Actualizado" : "Creado");
      setOpenForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/car-types/${id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-8" data-testid="car-types-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Tipos de auto
          </h1>
          <p className="text-slate-500 mt-1">
            Configura los precios base por tipo de vehículo.
          </p>
        </div>
        <Dialog open={openForm} onOpenChange={setOpenForm}>
          <DialogTrigger asChild>
            <Button
              onClick={openNew}
              disabled={!canManage}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
              data-testid="car-type-add-button"
            >
              <Plus className="w-4 h-4" /> Añadir tipo
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editing ? "Editar tipo de auto" : "Nuevo tipo de auto"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="mb-1.5 block">Nombre</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: SUV"
                  data-testid="car-type-name-input"
                />
              </div>
              <div>
                <Label className="mb-1.5 block">Precio (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  data-testid="car-type-price-input"
                />
              </div>
              <div>
                <Label className="mb-1.5 block">Descripción</Label>
                <Textarea
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  data-testid="car-type-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenForm(false)}>
                Cancelar
              </Button>
              <Button
                onClick={onSave}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="car-type-save-button"
              >
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              <th className="py-3 px-4">Nombre</th>
              <th className="py-3 px-4">Descripción</th>
              <th className="py-3 px-4 text-right">Precio</th>
              <th className="py-3 px-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr
                key={it.id}
                className="border-t border-slate-100"
                data-testid={`car-type-row-${it.id}`}
              >
                <td className="py-3 px-4 font-medium">{it.name}</td>
                <td className="py-3 px-4 text-slate-600">
                  {it.description || "—"}
                </td>
                <td className="py-3 px-4 text-right font-semibold tabular-nums">
                  {formatEUR(it.price)}
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="inline-flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(it)}
                      data-testid={`car-type-edit-${it.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          data-testid={`car-type-delete-${it.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar {it.name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción no se puede deshacer.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDelete(it.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="py-12 text-center text-slate-500"
                >
                  Aún no hay tipos de auto. Crea el primero.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
